import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MessageContactPresentationDto } from "@multi-chat/shared";
import { AttachmentCardShell } from "../AttachmentCardShell/AttachmentCardShell.js";
import { contactCardDisplayName, contactCardInitials } from "../contact-card.js";
import { MessageContactPreview } from "../MessageContactPreview/index.js";

export function MessageContact({
  organizationId,
  presentation,
}: {
  organizationId: number;
  presentation: MessageContactPresentationDto;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const name = contactCardDisplayName(presentation) ?? t("inbox.messageType.CONTACT");
  const initials = contactCardInitials(presentation);
  const details = [
    presentation.organization,
    presentation.primaryEmail,
    presentation.additionalPhoneCount
      ? t("inbox.contact.additionalPhones", { count: presentation.additionalPhoneCount })
      : null,
  ].filter((value): value is string => Boolean(value));

  return (
    <>
      <button
        type="button"
        className="block w-full border-0 bg-transparent p-0 text-start"
        onClick={() => setPreviewOpen(true)}
        aria-label={t("inbox.contact.previewOpen")}
      >
        <AttachmentCardShell>
          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-muted text-[0.75rem] font-semibold text-ink"
            aria-hidden="true"
          >
            {initials}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[0.8125rem] font-medium text-ink">{name}</span>
            {presentation.primaryPhone ? (
              <span className="mt-0.5 block truncate text-[11px] text-ink-subtle" dir="ltr">
                {presentation.primaryPhone}
              </span>
            ) : null}
            {details.map((line) => (
              <span key={line} className="mt-0.5 block truncate text-[11px] text-ink-subtle">
                {line}
              </span>
            ))}
          </span>
        </AttachmentCardShell>
      </button>
      <MessageContactPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        organizationId={organizationId}
        presentation={presentation}
      />
    </>
  );
}
